import { action, runInAction } from 'mobx';
import { observer } from 'mobx-react';
import { BusinessEntity, TrustEntityCodesEntity } from 'Models/Entities';
import * as React from 'react';
import { TextField } from 'Views/Components/TextBox/TextBox';
import { DatePicker } from '../DatePicker/DatePicker';
import If from '../If/If';
import RadioButtonGroupSetter from '../RadioButton/RadioButtonGroupSetter';
import { TextArea } from '../TextArea/TextArea';
import { AbnInput } from '../BusinessNumberInputs/AbnInput';
import { BusinessValidation, ExtraAbnDetail } from '../BusinessNumberInputs/BusinessValidationModel';
import { AcnInput } from '../BusinessNumberInputs/AcnInput';
import { ProvideTrusteeAcnOptions } from 'Models/Entities/BusinessEntity';


// We expect user to be an observable, but errors is managed through useState.
// It's awkward, but keeping user as an observable makes the input components work much more easily.
export interface OrganisationFieldsProps {
	businessEntity: BusinessEntity,
	disabled?: boolean,
	quickRegister?: boolean,
	setAbnAcnDetail?: React.Dispatch<React.SetStateAction<BusinessValidation>>
}

const OrganisationFields = observer((props: OrganisationFieldsProps) => {
	const {
		businessEntity,
		disabled,
		quickRegister,
		setAbnAcnDetail
	} = props;

	const [abnacnDetail] = React.useState<BusinessValidation>(new BusinessValidation({
		requiredAbn: true,
		abn: businessEntity.abn,
		acn: businessEntity.acn
	}));
	const [trusteeAcnDetail] = React.useState<BusinessValidation>(new BusinessValidation({
		acn: businessEntity.trusteeACN
	}));

	/**
	 * Handle for the change event for the inputs. This method will attempt to validate a given field
	 * iff there are existing errors. So that the user can immediately see when their input stops the error
	 * @param field The name of the field to be validated
	 * @param alwaysValidate If set to false, will only run the validation function if the given field already has an error
	 * We use this to prevent showing a new error before we're ready. (Like when a user has only just started typing into a field)
	 */
	const onChange = async (field: string, alwaysValidate: boolean = false) => {
		if (alwaysValidate || !!businessEntity.errors[field]) {
			await businessEntity.validateField(field);
		}

		// Handle optional fields (like email, business description, etc.)
		const fieldValue = (businessEntity as any)[field] || '';
		const optionalFields = ['email', 'summary'];

		if (optionalFields.includes(field) && (fieldValue.trim() === '')) {
			runInAction(() => delete businessEntity.errors[field]);
		}
	};

	const onAfterAbnAcnChange = (newValue: BusinessValidation) => {
		runInAction(async () => {
			//@ts-ignore
			[businessEntity.abn, businessEntity.acn] = [newValue.abn, newValue.acn];
			await businessEntity.prefillAbnFieldsFromAbrDetails(newValue.abnExtraDetails);
		})
		if (setAbnAcnDetail) setAbnAcnDetail(newValue);
	}

	const onAfterTrusteeAcnChange = (newValue: BusinessValidation) => {
		// @ts-ignore
		runInAction(() => businessEntity.trusteeACN = newValue.acn)
	}

	const diplayConditionalTrusteeQuestion = TrustEntityCodesEntity.trusteeCodesSync.includes(abnacnDetail?.abnExtraDetails?.typeCode ?? "");

	return (
		<div className="organisation-fields">

			{/* Idk why but need this div here for styling */}
			<div>
				<AbnInput model={abnacnDetail} isRequired onAfterChange={onAfterAbnAcnChange} additionalInputPropTypes={{disabled: disabled}} />
			</div>

			{/* Display org name after ABNInput as as we auto-fill organisation name from abn acn details */}
			<TextField
				className="business-name"
				model={businessEntity}
				modelProperty="name"
				label={`Organisation name`}
				placeholder="Acme Pty Ltd"
				onChangeAndBlur={async () => { await onChange('name', true); }}
				onAfterChange={async () => { await onChange('name'); }}
				errors={businessEntity.errors.name}
				isRequired
				isDisabled={disabled}
			/>

			{/* Idk why but need this div here for styling */}
			{!quickRegister && <div><AcnInput model={abnacnDetail} onAfterChange={onAfterAbnAcnChange} additionalInputPropTypes={{disabled: disabled}} /></div>}

			{!quickRegister && (
				<TextField
					className="phone"
					model={businessEntity}
					modelProperty="phone"
					label="Business phone number"
					placeholder="1300 123 456"
					onChangeAndBlur={async () => { await onChange('phone', true); }}
					onAfterChange={async () => { await onChange('phone'); }}
					errors={businessEntity.errors.phone}
					isDisabled={disabled}
				/>
			)}

			{/* if the entity typecode is an individual show the date of birth */}
			<If condition={abnacnDetail.abnExtraDetails?.typeCode === 'IND'}>
				<DatePicker
					model={businessEntity}
					modelProperty="dateOfBirth"
					label="Sole trader's date of birth"
					placeholder="Date of birth"
					onChangeAndBlur={() => onChange('dateOfBirth', true)}
					onAfterChange={() => onChange('dateOfBirth')}
					errors={businessEntity.errors.dateOfBirth}
					isRequired
					isDisabled={disabled}
				/>
			</If>

			{/* if the entity typecode is a trust show the radio button */}
			<If condition={diplayConditionalTrusteeQuestion}>
				<RadioButtonGroupSetter
					className="trusteeRadio"
					value={businessEntity.useCorporateTrusteeACN}
					setValue={newValue => {
						runInAction(() => {
							businessEntity.useCorporateTrusteeACN = newValue;
						});
					}}
					options={ProvideTrusteeAcnOptions}
					onAfterSet={() => {
						runInAction(() => {
							businessEntity.trusteeName = '';
							//@ts-ignore
							businessEntity.trusteeACN = null;
						});
					}}
					label="Do you have a trustee Australian Company Number (ACN)?"
					// the error on this radio button is if the user has pressed next before choosing an
					// option. so both trusteeName and trustee acn are empty meaning this radio button has an error
					errors={(businessEntity.errors.trusteeName === 'Trustee name cannot be empty'
						&& businessEntity.errors.trusteeACN === 'Trustee ACN cannot be empty'
						&& !businessEntity.useCorporateTrusteeACN ? 'An option must be selected' : undefined)}
					isRequired
					isDisabled={disabled}
				/>

				{/* display the ACN input if the radio button was yes */}
				<If condition={businessEntity.useCorporateTrusteeACN === 'Yes'}>
					{/* Idk why but need this div here for styling */}
					<div><AcnInput model={trusteeAcnDetail} label='Trustee ACN' onAfterChange={onAfterTrusteeAcnChange} additionalInputPropTypes={{disabled: disabled}} /></div>
				</If>

				{/* display the trustee name input if the radio button was no */}
				<If condition={businessEntity.useCorporateTrusteeACN === 'No'}>
					<TextField
						className="trustee-name"
						model={businessEntity}
						modelProperty="trusteeName"
						label="Trustee name"
						placeholder="Trustee name"
						onChangeAndBlur={action(async () => {
							await onChange('trusteeName', true);
							runInAction(() => {
								delete businessEntity.errors.trusteeACN;
							});
						})}
						onAfterChange={action(async () => {
							await onChange('trusteeName');
							runInAction(() => {
								delete businessEntity.errors.trusteeACN;
							});
						})}
						errors={businessEntity.errors.trusteeName}
						isRequired
						isDisabled={disabled}
					/>
				</If>
			</If>

			<If condition={quickRegister}>
				<TextArea
					className="business-description"
					model={businessEntity}
					modelProperty="summary"
					label="Tell us a bit about your business"
					placeholder="Describe your business, industry, and the products you’re interested in."
					onChangeAndBlur={() => onChange('summary', true)}
					onAfterChange={() => onChange('summary')}
					errors={businessEntity.errors.summary}
					isDisabled={disabled}
				/>
			</If>
			{/* If user selects use organisation email, we disable the input and use primary business entity value */}
			<TextField
				className="email"
				model={businessEntity}
				modelProperty="email"
				label={`Organisation email`}
				placeholder="hello@company.com"
				onChangeAndBlur={() => onChange('email', true)}
				onAfterChange={() => onChange('email')}
				errors={businessEntity.errors.email}
				isDisabled={disabled || businessEntity.useOrganisationEmail}
				isRequired={quickRegister}
			/>

			{!quickRegister && !!businessEntity.address && (
				<>
					<TextField
						className="line-1"
						label="Street address"
						model={businessEntity.address}
						modelProperty="line1"
						onChangeAndBlur={() => onChange('line1', true)}
						onAfterChange={() => onChange('line1')}
						isDisabled={disabled}
						placeholder="Main St"
					/>
					<TextField
						className="line-2"
						label="Unit, suite, or building"
						model={businessEntity.address}
						modelProperty="line2"
						onChangeAndBlur={() => onChange('line2', true)}
						onAfterChange={() => onChange('line2')}
						isDisabled={disabled}
						placeholder="Suite 2, Building 5"
					/>
					<TextField
						className="suburb half-width"
						label="Suburb"
						model={businessEntity.address}
						modelProperty="suburb"
						onChangeAndBlur={() => onChange('suburb', true)}
						onAfterChange={() => onChange('suburb')}
						isDisabled={disabled}
						placeholder="Brisbane"
					/>
					<TextField
						className="state half-width"
						label="State"
						model={businessEntity.address}
						modelProperty="state"
						onChangeAndBlur={() => onChange('state', true)}
						onAfterChange={() => onChange('state')}
						isDisabled={disabled}
						placeholder="QLD"
					/>
					<TextField
						className="postcode half-width"
						label="Postal code"
						model={businessEntity.address}
						modelProperty="postcode"
						errors={businessEntity.errors['address.postcode']}
						onAfterChange={() => onChange('address.postcode')}
						onChangeAndBlur={() => onChange('address.postcode', true)}
						isDisabled={disabled}
						placeholder="4000"
					/>
					<TextField
						className="country half-width"
						label="Country"
						model={businessEntity.address}
						modelProperty="country"
						onChangeAndBlur={() => onChange('country', true)}
						onAfterChange={() => onChange('country')}
						isDisabled={disabled}
						placeholder="Australia"
					/>
				</>
			)}
		</div>
	);
});

export default OrganisationFields;
